import { providerTenantsService } from '../../services/ProviderTenantsService';
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { AppButton, AppTextField } from '../design-system';
import { EmailField } from '../forms';
import { validateData, formatValidationErrors } from '../../utils/validation';
import { CreateTenantRequestSchema, type CreateTenantRequest } from '../../../../shared/src/schemas';
import * as dt from '../../../../shared/src/data.types';

interface CreateTenantDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onTenantCreated: () => void;
}

const defaultFormData = (): CreateTenantRequest => ({
  name: '',
  subdomain: '',
  adminEmail: '' as dt.Email,
  adminFirstName: '' as dt.FirstName,
  adminLastName: '' as dt.LastName,
  customerManagedEncryption: false,
  customerKmsKeyArn: undefined,
  customerKmsKeyAlias: undefined,
  customerExternalId: undefined,
});

const CreateTenantDialog: React.FC<CreateTenantDialogProps> = ({
  open,
  onClose,
  onSuccess,
  onError,
  onTenantCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateTenantRequest>(defaultFormData());

  const handleSubmit = async () => {
    if (loading) return;

    try {
      setLoading(true);

      const validation = validateData(CreateTenantRequestSchema, formData);
      if (!validation.success) {
        onError(`Validation failed:\n${formatValidationErrors(validation.errors || [])}`);
        setLoading(false);
        return;
      }

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out after 60 seconds')), 60000)
      );

      const result = await Promise.race([
        providerTenantsService.createTenant({
          name: validation.data!.name,
          subdomain: validation.data!.subdomain,
          adminEmail: validation.data!.adminEmail as dt.Email,
          adminFirstName: validation.data!.adminFirstName as dt.FirstName,
          adminLastName: validation.data!.adminLastName as dt.LastName,
          customerManagedEncryption: validation.data!.customerManagedEncryption,
          customerKmsKeyArn: validation.data!.customerKmsKeyArn,
          customerKmsKeyAlias: validation.data!.customerKmsKeyAlias,
          customerExternalId: validation.data!.customerExternalId,
        }),
        timeoutPromise,
      ]) as Awaited<ReturnType<typeof providerTenantsService.createTenant>>;

      setFormData(defaultFormData());
      setLoading(false);
      onClose();
      onSuccess(
        `Tenant "${result.tenant.name}" created successfully! Invitation email sent to ${result.tenant.adminUser.email}`
      );
      onTenantCreated();
    } catch (error) {
      setLoading(false);
      onError('Failed to create tenant: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleClose = () => {
    setFormData(defaultFormData());
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Tenant</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <AppTextField
            label="Tenant Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
          />
          <AppTextField
            label="Subdomain"
            value={formData.subdomain}
            onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
            fullWidth
            required
            helperText="Used in tenant URL path (e.g., app.cnapse.ai/acme)"
          />
          <EmailField
            label="Admin Email"
            value={formData.adminEmail}
            onChange={(value) => setFormData({ ...formData, adminEmail: value as dt.Email })}
            fullWidth
            required
          />
          <AppTextField
            label="Admin First Name"
            value={formData.adminFirstName}
            onChange={(e) => setFormData({ ...formData, adminFirstName: e.target.value as dt.FirstName })}
            fullWidth
            required
          />
          <AppTextField
            label="Admin Last Name"
            value={formData.adminLastName}
            onChange={(e) => setFormData({ ...formData, adminLastName: e.target.value as dt.LastName })}
            fullWidth
            required
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Data Encryption
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              All tenants receive a dedicated encryption key managed by the platform by default.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.customerManagedEncryption}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      customerManagedEncryption: e.target.checked,
                      ...(e.target.checked
                        ? {}
                        : {
                            customerKmsKeyArn: undefined,
                            customerKmsKeyAlias: undefined,
                            customerExternalId: undefined,
                          }),
                    })
                  }
                />
              }
              label="Customer-managed encryption (BYOK)"
            />

            {formData.customerManagedEncryption && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Customer KMS Key Configuration
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <AppTextField
                    label="KMS Key ARN"
                    value={formData.customerKmsKeyArn || ''}
                    onChange={(e) => setFormData({ ...formData, customerKmsKeyArn: e.target.value })}
                    fullWidth
                    required
                    placeholder="arn:aws:kms:us-east-1:123456789012:key/12345678-1234-1234-1234-123456789012"
                    helperText="AWS KMS Key ARN provided by the customer"
                    size="small"
                  />
                  <AppTextField
                    label="External ID"
                    value={formData.customerExternalId || ''}
                    onChange={(e) => setFormData({ ...formData, customerExternalId: e.target.value })}
                    fullWidth
                    required
                    helperText="External ID for cross-account KMS access"
                    size="small"
                  />
                  <AppTextField
                    label="Key Alias (optional)"
                    value={formData.customerKmsKeyAlias || ''}
                    onChange={(e) => setFormData({ ...formData, customerKmsKeyAlias: e.target.value })}
                    fullWidth
                    size="small"
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AppButton onClick={handleClose} disabled={loading}>
          Cancel
        </AppButton>
        <AppButton variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Create Tenant'}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTenantDialog;
